import { A } from '@adornis/base/env-info.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import type { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { DoesNotExistAspect, ExistsAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import type { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import type { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ExerciseType } from '@chancenwerk/enums/enums.js';
import { endOfDay, startOfDay } from 'date-fns';
import { firstValueFrom, from, of, switchMap } from 'rxjs';
import { Adresse } from '../../../adresse.js';
import { CampusLesson } from '../../../campus-learning-content/entities/CampusLesson.js';
import { ContentResultEventlog } from '../../../eventlogs/ContentResultEventlog.js';
import { ChancenwerkUserFilter } from '../../../filter/ChancenwerkUserFilter.js';
import type { ChancenRoleAspect } from '../../../semantics/ChancenRoleAspect.js';
import { ChancenRoleMultiAspect } from '../../../semantics/ChancenRoleMultiAspect.js';
import { Role } from '../../../semantics/Role.js';
import { StudentParticipationRelationship } from '../../../semantics/StudentParticipationRelationship.js';
import type { KPFAspect } from '../../kpf/entities/aspects/KPFAspect.js';
import { KPFMultipleAspect } from '../../kpf/entities/aspects/KPFMultipleAspect.js';
import { KPF } from '../../kpf/entities/KPF.js';
import type { PartnerAspect } from '../../partner/entities/aspects/PartnerAspect.js';
import { Partner } from '../../partner/entities/Partner.js';
import { ChancenwerkUser } from '../entities/ChancenwerkUser.js';
import type { ExternalContactPerson } from '../entities/ExternalContactPerson.js';
import { ExternerMitarbeiter } from '../entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '../entities/Mitarbeiter.js';
import type { Student } from '../entities/Student.js';
import { VerwaltungsMitarbeiter } from '../entities/VerwaltungsMitarbeiter.js';

function studentPartnerLookup() {
  return [
    {
      $lookup: {
        from: StudentParticipationRelationship._collectionName,
        let: { userID: '$_id' },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ['$_class', StudentParticipationRelationship._class] },
                  { $eq: ['$subjectID', '$$userID'] },
                ],
              },
            },
          },
        ],
        as: 'relations',
      },
    },
    {
      $lookup: {
        from: KPF._collectionName,
        let: { objectIDs: '$relations.objectID' },
        pipeline: [
          { $match: { $expr: { $in: ['$_id', '$$objectIDs'] } } },
          { $group: { _id: null, objects: { $push: '$$ROOT' } } },
          { $project: { _id: 0, objects: { schoolID: 1, produkt: { _class: 1 } } } },
        ],
        as: 'kpfMultiples',
      },
    },
    { $addFields: { kpfMultiples: { $arrayElemAt: ['$kpfMultiples.objects', 0] } } },
    { $addFields: { kpf_object: { $arrayElemAt: ['$kpfMultiples', 0] } } },
    {
      $lookup: {
        from: Partner._collectionName,
        localField: 'kpf_object.schoolID',
        foreignField: '_id',
        as: 'partners',
      },
    },
    { $addFields: { partner: { $arrayElemAt: ['$partners', 0] } } },
    { $addFields: { partnerName: '$partner.name' } },
  ];
}

async function constructUserIDFilterFromLessons(lessonIDs: string[]) {
  if (!lessonIDs?.length) return [];
  const fullMatchFilter = await publishFilter(CampusLesson).then(pf => {
    const baseFilter = { _id: { $in: lessonIDs }, hiddenFromStudents: { $ne: true } };
    if (pf && Object.keys(pf).length > 0) return { $and: [pf, baseFilter] };
    else return baseFilter;
  });
  const userIDs = (
    await (
      await getRawCollection<CampusLesson>(CampusLesson._collectionName)
    )
      .aggregate<{ _id: string }>([
        { $match: fullMatchFilter },
        { $lookup: { from: 'eventlog', localField: '_id', foreignField: 'lessonID', as: 'eventlogs' } },
        { $unwind: '$eventlogs' },
        {
          $group: {
            _id: { userID: '$eventlogs.userID', lessonID: '$_id', exerciseIDs: '$exerciseIDs' },
            eventlogsize: { $sum: 1 },
          },
        },
        {
          $match: {
            $expr: {
              $and: [
                { $gt: ['$eventlogsize', 0] },
                { $gt: [{ $size: '$_id.exerciseIDs' }, 0] },
                { $ne: ['$_id.userID', null] },
              ],
            },
          },
        },
        { $group: { _id: '$_id.userID' } },
      ])
      .toArray()
  ).map(a => a._id);
  return [{ $match: { _id: { $in: userIDs } } }];
}

const resolveSubscribeUsersByFilter = <T extends ChancenwerkUser[] | number>(
  skip: number,
  limit: number,
  filter: ChancenwerkUserFilter,
  onlyReturnCount = false,
  exportMode = false,
) => {
  return (
    gqlFields: BaseQLSelectionSet<
      ChancenwerkUser | Student | VerwaltungsMitarbeiter | ExternalContactPerson | ExternerMitarbeiter
    >,
  ) => {
    return from(publishFilter(ChancenwerkUser._class)).pipe(
      switchMap(publishFilter => {
        const selector = publishFilter ? { $and: [publishFilter, filter.getMongoFilter()] } : filter.getMongoFilter();
        return of(selector);
        // return watchEntireCollection('adornisuser-materialized').pipe(map(() => selector));
      }),
      // eslint-disable-next-line complexity
      switchMap(async basicSelector => {
        const roleAspect = filter.getFilterAspect<ChancenRoleAspect>('role');
        const rolesAspect = filter.getFilterAspect<ChancenRoleMultiAspect | DoesNotExistAspect | ExistsAspect>(
          'semanticRoles',
        );
        const activeAspect = filter.getFilterAspect<BooleanAspect>('active');
        const kpfAspect = filter.getFilterAspect<KPFAspect>('kpfs');
        const kpfMultipleAspect = filter.getFilterAspect<KPFMultipleAspect | DoesNotExistAspect | ExistsAspect>(
          'kpfsMultiple',
        );
        const partnerRelationAspect = filter.getFilterAspect<PartnerAspect>('partnerRelations');
        const chancenIDAspect = filter.getFilterAspect<StringAspect>('chancenID');
        const lastExerciseDateAspect = filter.getFilterAspect<DateTimeAspect>('resultInfo');

        const workshopID = filter.getFilterAspect<ListAspect>('workshopFilter');
        const workshopDate = filter.getFilterAspect<DateTimeAspect>('workshopDate');
        const workshopStatus = filter.getFilterAspect<ListAspect>('workshopStatus');
        const workshopExists = filter.getFilterAspect<BooleanAspect>('workshopExists');
        const workshopIsNotTraining = filter.getFilterAspect<BooleanAspect>('workshopIsNotTraining');
        const needWorkshopLookup =
          workshopID?.values?.length ||
          workshopDate?.firstValue ||
          workshopStatus?.values?.length ||
          workshopIsNotTraining?.value;

        const lessonIDs = filter.getFilterAspect<StringAspect>('lessons')?.value?.split(',').filter(Boolean);
        const abweichendeSchule = filter.getFilterAspect<StringAspect>('abweichendeSchule')?.value;

        const rolesToFilter =
          rolesAspect instanceof ChancenRoleMultiAspect ? (rolesAspect?.values ?? ([] as string[])) : ([] as string[]);
        if (roleAspect?.value) rolesToFilter.push(roleAspect.value ?? '');

        const kpfsToFilter =
          kpfMultipleAspect instanceof KPFMultipleAspect ? (kpfMultipleAspect.values ?? ([] as string[])) : [];
        if (kpfAspect?.value) kpfsToFilter.push(kpfAspect.value ?? '');

        // get all user ids from which have a role which matches the filter
        const relevantExerciseTypes = [
          ExerciseType.LEVUMIGERMAN,
          ExerciseType.LEVUMIMATH,
          ExerciseType.LEVUMI_NUMBER_DICTATE,
        ];

        if (chancenIDAspect) chancenIDAspect.fieldName = 'chancenID';
        const chancenIDFilter = chancenIDAspect?.getMongoFilter();

        const relationFilterArray = [
          ...(kpfsToFilter.length ? [{ $in: ['$$relation.objectID', kpfsToFilter] }] : []),
          ...(rolesToFilter.length ? [{ $in: ['$$relation.predicateID', rolesToFilter] }] : []),
        ];

        const aggregation = [
          {
            $addFields: {
              vornameCleaned: { $replaceAll: { input: '$vorname', find: 'é', replacement: 'e' } },
              nachnameCleaned: { $replaceAll: { input: '$nachname', find: 'é', replacement: 'e' } },
            },
          },
          ...(lessonIDs?.length ? await constructUserIDFilterFromLessons(lessonIDs) : []),
          ...(lastExerciseDateAspect?.firstValue
            ? [
                {
                  $lookup: {
                    from: ContentResultEventlog._collectionName,
                    localField: '_id',
                    foreignField: 'userID',
                    as: 'logs',
                    pipeline: [{ $match: { type: { $in: relevantExerciseTypes } } }, { $sort: { createdAt: 1 } }],
                  },
                },
                { $addFields: { log: { $last: '$logs' } } },
                { $unset: 'logs' },
              ]
            : []),

          {
            $addFields: {
              chancenID: { $ifNull: ['$employeeID', '$studentID'] },
              firstRelation: {
                $ifNull: [{ $arrayElemAt: ['$activeRelations', 0] }, { $arrayElemAt: ['$inactiveRelations', 0] }],
              },
            },
          },

          {
            $addFields: {
              kpfKuerzel: {
                $concat: [
                  { $toString: { $ifNull: ['$firstRelation.object.school.kuerzel', ''] } },
                  '_',
                  { $toString: { $ifNull: ['$firstRelation.object.produkt.kuerzel', ''] } },
                ],
              },
            },
          },

          ...((partnerRelationAspect?.value?.length ?? 0) > 0
            ? [
                {
                  $lookup: {
                    from: 'semantic-relationships', // remove cyclic dependency
                    localField: '_id',
                    foreignField: 'subjectID',
                    as: 'partnerRelationships',
                    pipeline: [
                      {
                        $match: {
                          _class: 'PartnerRelationship',
                          objectID: { $in: partnerRelationAspect?.value ?? [] },
                        },
                      },
                    ],
                  },
                },
                { $match: { partnerRelationships: { $exists: true, $ne: [] } } },
              ]
            : []),

          ...((abweichendeSchule?.length ?? 0) > 0
            ? [
                {
                  $lookup: {
                    from: 'partners', // remove cyclic dependency
                    localField: 'schoolInformation.abweichendeSchuleID',
                    foreignField: '_id',
                    as: 'abweichendeSchule',
                  },
                },
                {
                  $match: {
                    $or: [
                      { 'abweichendeSchule.name': { $regex: abweichendeSchule, $options: 'i' } },
                      { 'abweichendeSchule.kuerzel': { $regex: abweichendeSchule, $options: 'i' } },
                      { 'schoolInformation.abweichendeSchule': { $regex: abweichendeSchule, $options: 'i' } },
                    ],
                  },
                },
              ]
            : []),

          ...(needWorkshopLookup
            ? [
                {
                  $lookup: {
                    from: 'workshop-attendance', // remove cyclic dependency
                    localField: '_id',
                    foreignField: 'userID',
                    as: 'workshopAttendances',
                    pipeline: [
                      { $lookup: { from: 'workshops', localField: 'workshopID', foreignField: '_id', as: 'workshop' } },
                      { $addFields: { workshop: { $arrayElemAt: ['$workshop', 0] } } },
                    ],
                  },
                },
              ]
            : []),

          ...(needWorkshopLookup
            ? (() => {
                if (workshopExists && workshopExists.value === false && workshopID?.values) {
                  return [
                    {
                      $match: {
                        $nor: [
                          {
                            workshopAttendances: {
                              $elemMatch: {
                                ...(workshopID?.values?.at(0) ? { workshopID: { $in: workshopID.values } } : {}),
                              },
                            },
                          },
                        ],
                      },
                    },
                  ];
                }

                return [
                  {
                    $match: {
                      workshopAttendances: {
                        $elemMatch: {
                          ...(workshopID?.values?.at(0) ? { workshopID: { $in: workshopID.values } } : {}),
                          ...(workshopDate?.firstValue || workshopDate?.secondValue
                            ? {
                                $and: [
                                  ...(workshopDate?.firstValue
                                    ? [{ date: { $gte: startOfDay(workshopDate.firstValue) } }]
                                    : []),
                                  ...(workshopDate?.secondValue
                                    ? [{ date: { $lte: endOfDay(workshopDate.secondValue) } }]
                                    : []),
                                ],
                              }
                            : {}),
                          $or: [
                            {
                              'workshop.requiresTraining': true,
                              ...(!workshopIsNotTraining?.value ? { _class: 'WorkshopTrainingAttendance' } : {}),
                              ...(workshopIsNotTraining?.value === true ? { _class: 'WorkshopAttendance' } : {}),
                              ...(workshopStatus?.values && !workshopIsNotTraining?.value
                                ? { state: workshopStatus.values }
                                : {}),
                            },
                            {
                              'workshop.requiresTraining': { $ne: true },
                              _class: 'WorkshopAttendance',
                              ...(workshopStatus?.values ? { state: { $in: workshopStatus.values } } : {}),
                            },
                          ],
                        },
                      },
                    },
                  },
                ];
              })()
            : []),

          // Build up relations arrays
          ...(relationFilterArray.length
            ? [
                {
                  $addFields: {
                    activeRelationsNarrowed: {
                      $filter: { input: '$activeRelations', as: 'relation', cond: { $and: relationFilterArray } },
                    },
                    inactiveRelationsNarrowed: {
                      $filter: { input: '$inactiveRelations', as: 'relation', cond: { $and: relationFilterArray } },
                    },
                  },
                },
              ]
            : []),

          {
            $match: {
              $and: [
                basicSelector,
                ...(chancenIDFilter ? [chancenIDFilter] : []),

                ...(activeAspect?.value === true && !relationFilterArray.length
                  ? [{ 'activeRelations.0': { $exists: true } }]
                  : []),
                ...(activeAspect?.value === false && !relationFilterArray.length
                  ? [{ 'activeRelations.0': { $exists: false } }]
                  : []),

                ...(activeAspect?.value === true && relationFilterArray.length
                  ? [{ 'activeRelationsNarrowed.0': { $exists: true } }]
                  : []),
                ...(activeAspect?.value === false && relationFilterArray.length
                  ? [{ 'inactiveRelationsNarrowed.0': { $exists: true } }]
                  : []),

                ...(((activeAspect?.value === null || activeAspect?.value === undefined) &&
                  relationFilterArray.length) ||
                kpfsToFilter.length
                  ? [
                      {
                        $or: [
                          { 'activeRelationsNarrowed.0': { $exists: true } },
                          { 'inactiveRelationsNarrowed.0': { $exists: true } },
                        ],
                      },
                    ]
                  : []),

                ...(kpfMultipleAspect instanceof ExistsAspect || rolesAspect instanceof ExistsAspect
                  ? [{ 'activeRelations.0': { $exists: true } }]
                  : []),

                ...(kpfMultipleAspect instanceof DoesNotExistAspect || rolesAspect instanceof DoesNotExistAspect
                  ? [{ activeRelations: { $size: 0 } }]
                  : []),

                ...(lastExerciseDateAspect?.firstValue
                  ? [
                      {
                        $or: [
                          { 'log.createdAt': { $exists: true, $lte: lastExerciseDateAspect.firstValue } },
                          { log: { $exists: false } },
                        ],
                      },
                    ]
                  : []),
              ],
            },
          },

          ...(onlyReturnCount
            ? [{ $project: { _id: 1 } }, { $count: 'count' }]
            : [
                ...(filter.sortingFields.includes('partnerName') ? studentPartnerLookup() : []),

                ...(() => {
                  const sortObject = {
                    // * Sort for chancenID
                    ...(filter.sortingFields.includes('chancenID')
                      ? {
                          chancenID:
                            filter.sortingDirections[filter.sortingFields.indexOf('chancenID')] === 'asc' ? 1 : -1,
                        }
                      : {}),

                    // * Sort by first KPF
                    ...(filter.sortingFields.includes('kpfsMultiple')
                      ? {
                          kpfKuerzel:
                            filter.sortingDirections[filter.sortingFields.indexOf('kpfsMultiple')] === 'asc' ? 1 : -1,
                        }
                      : {}),

                    // * Sorting of rest
                    ...(() => {
                      const sort = filter.getMongoOptions().sort;
                      delete sort.kpfsMultiple;

                      if (Object.keys(sort).length === 0) {
                        return {};
                      }

                      return sort;
                    })(),
                  };

                  return Object.keys(sortObject).length > 0 ? [{ $sort: sortObject }] : [];
                })(),

                ...(skip ? [{ $skip: skip }] : []),
                ...(limit ? [{ $limit: limit }] : []),
                { $project: gqlFields },
              ]),
        ];

        // * If we want to use this in a migration we can't call getMyself function, as this will throw in serverContext
        const currentUser = !context.serverContext
          ? await CurrentUserInfo.getMyself<ChancenwerkUser>()({
              _id: 1,
              roles: { name: 1, permissions: 1, contexts: 1 },
            })
          : null;

        const collectionName = [
          ...(exportMode ? ['tmp'] : []),
          'adornisuser-materialized',
          ...(exportMode ? [currentUser?._id ?? A.getGloballyUniqueID()] : []),
        ].join('.');

        // * Copy over the original collection to a temp one at the start
        if (exportMode && skip === 0) {
          const originalCollection = await getRawCollection<EntityData<ChancenwerkUser>>('adornisuser-materialized');
          await originalCollection.aggregate([{ $out: collectionName }]).toArray();
        }

        const collection = await getRawCollection<EntityData<ChancenwerkUser>>(collectionName);
        const agg = await collection.aggregate<EntityData<ChancenwerkUser>>(aggregation).toArray();

        // * Drop the temp collection at the end
        if (exportMode && agg.length < limit) {
          await collection.drop();
        }

        if (onlyReturnCount) {
          return ((agg[0] ?? { count: 0 }) as any).count as T;
        }

        // @ts-expect-error f you T is an array here
        return (agg as T).map((user: ChancenwerkUser) => {
          if (
            currentUser?.checkRole(Role.SCHUKO) &&
            [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class].includes(user._class)
          ) {
            user.adresse = new Adresse({});
          }
          return user;
        });
      }),
    );
  };
};

export const subscribeUsersByFilter = registerSubscription({
  type: () => [ChancenwerkUser],
  operationName: 'subscribeUsersByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => ChancenwerkUserFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: ChancenwerkUserFilter) =>
    resolveSubscribeUsersByFilter<ChancenwerkUser[]>(skip, limit, filter),
});

export const subscribeUsersByFilterExport = registerSubscription({
  type: () => [ChancenwerkUser],
  operationName: 'subscribeUsersByFilterExport',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => ChancenwerkUserFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: ChancenwerkUserFilter) =>
    resolveSubscribeUsersByFilter<ChancenwerkUser[]>(skip, limit, filter, false, true),
});

export const subscribeCountUsersByFilter = registerSubscription({
  type: () => Int,
  operationName: 'subscribeCountUsersByFilter',
  params: [{ type: () => ChancenwerkUserFilter, name: 'filter' }],
  resolve: (filter: ChancenwerkUserFilter) => () => resolveSubscribeUsersByFilter<number>(-1, -1, filter, true)({}),
});

export const getCountUsersByFilter = registerQuery({
  type: () => Int,
  operationName: 'getCountUsersByFilter',
  params: [{ type: () => ChancenwerkUserFilter, name: 'filter' }],
  resolve: (filter: ChancenwerkUserFilter) => async () => {
    try {
      const count = await firstValueFrom(resolveSubscribeUsersByFilter<number>(-1, -1, filter, true)({}));
      return count as number;
    } catch (e: any) {
      return 0;
    }
  },
});
